import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/hooks/useTheme';

const ROLES = [
  { key: 'generator_owner', labelKey: 'ob_s5_role_generator_owner', descKey: 'ob_s5_role_generator_owner_desc' },
  { key: 'supplier', labelKey: 'ob_s5_role_supplier', descKey: 'ob_s5_role_supplier_desc' },
  { key: 'trader', labelKey: 'ob_s5_role_trader', descKey: 'ob_s5_role_trader_desc' },
  { key: 'consumer', labelKey: 'ob_s5_role_consumer', descKey: 'ob_s5_role_consumer_desc' },
  { key: 'balancing_group_member', labelKey: 'ob_s5_role_balancing_group_member', descKey: 'ob_s5_role_balancing_group_member_desc' },
  { key: 'bess_operator', labelKey: 'ob_s5_role_bess_operator', descKey: 'ob_s5_role_bess_operator_desc' },
  { key: 'service_provider', labelKey: 'ob_s5_role_service_provider', descKey: 'ob_s5_role_service_provider_desc' },
];

interface StepRolesProps {
  selected: string[];
  onChange: (selected: string[]) => void;
  errors: Record<string, string>;
}

export default function StepRoles({ selected, onChange, errors }: StepRolesProps) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [touched, setTouched] = useState(false);

  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#8E8E93' : '#6B6B70';
  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const borderDefault = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const selectedBg = isDark ? 'var(--surface-2)' : 'var(--surface-2)';
  const selectedPillsBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const mutedText = isDark ? '#6B6B70' : '#A0A0A8';

  const toggleRole = (key: string) => {
    setTouched(true);
    if (selected.includes(key)) {
      onChange(selected.filter((r) => r !== key));
    } else {
      onChange([...selected, key]);
    }
  };

  return (
    <div className="space-y-5">
      <div className="mb-6">
        <h2 className="text-xl font-semibold" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>
          {t('ob_s5_title')}
        </h2>
        <p className="text-sm mt-1" style={{ color: textMuted }}>
          {t('ob_s5_subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {ROLES.map((role) => {
          const isSelected = selected.includes(role.key);
          return (
            <button
              key={role.key}
              onClick={() => toggleRole(role.key)}
              className="flex items-start gap-3 p-4 rounded-lg border text-left transition-all duration-150 cursor-pointer"
              style={{
                borderColor: isSelected ? 'var(--accent)' : borderDefault,
                background: isSelected ? selectedBg : cardBg,
              }}
              onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.borderColor = 'var(--accent)'; }}
              onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.borderColor = borderDefault; }}
            >
              <div
                className="w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-0.5 transition-colors"
                style={{
                  borderColor: isSelected ? 'var(--accent)' : borderDefault,
                  background: isSelected ? 'var(--accent)' : cardBg,
                }}
              >
                {isSelected && <i className="ri-check-line text-xs" style={{ color: '#fff' }}></i>}
              </div>
              <div>
                <div className="text-sm font-medium" style={{ color: textMain }}>{t(role.labelKey)}</div>
                <div className="text-xs mt-0.5" style={{ color: textMuted }}>{t(role.descKey)}</div>
              </div>
            </button>
          );
        })}
      </div>

      {touched && errors.roles && (
        <p className="text-xs mt-2" style={{ color: 'var(--accent)' }}>{errors.roles}</p>
      )}

      <div className="mt-4 p-3 rounded-md" style={{ background: selectedPillsBg }}>
        <div className="text-xs font-medium mb-1" style={{ color: textMuted }}>
          {t('ob_s5_selected')} ({selected.length}):
        </div>
        <div className="flex flex-wrap gap-1.5">
          {selected.length === 0 && (
            <span className="text-xs" style={{ color: mutedText }}>{t('ob_s5_none')}</span>
          )}
          {selected.map((key) => {
            const role = ROLES.find((r) => r.key === key);
            return (
              <span key={key} className="text-xs px-2 py-1 rounded-full font-medium" style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}>
                {role ? t(role.labelKey) : key}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}